import { useState, useEffect } from "react";
import axios from "axios";
import { Mail, CheckCircle2, XCircle, FileText, Image as ImageIcon, ChevronDown, ChevronUp } from "lucide-react";
import { format } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailHistory = ({ workOrderId, refreshKey = 0 }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (!workOrderId) return;
    setLoading(true);
    axios
      .get(`${API}/work-orders/${workOrderId}/email-history`)
      .then((res) => setHistory(res.data || []))
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  }, [workOrderId, refreshKey]);

  const formatDT = (iso) => {
    try {
      return format(new Date(iso), "MMM dd, yyyy 'at' HH:mm");
    } catch {
      return iso;
    }
  };

  return (
    <div className="space-y-3" data-testid="email-history-section">
      <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-[#71717A]">
        <Mail className="w-4 h-4" />
        Email History
        {history.length > 0 && (
          <span className="text-[#09090B] font-semibold" data-testid="email-history-count">
            ({history.length})
          </span>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-[#71717A]" data-testid="email-history-loading">Loading history...</p>
      ) : history.length === 0 ? (
        <p className="text-sm text-[#71717A] italic" data-testid="email-history-empty">
          No emails sent yet for this work order.
        </p>
      ) : (
        <div className="space-y-2" data-testid="email-history-list">
          {history.map((entry) => {
            const expanded = expandedId === entry.id;
            const isSent = entry.status === "sent";
            return (
              <div
                key={entry.id}
                className={`border rounded-sm ${
                  isSent ? "border-[#A7F3D0] bg-[#F0FDF4]" : "border-[#FECACA] bg-[#FEF2F2]"
                }`}
                data-testid={`email-history-entry-${entry.id}`}
              >
                <button
                  type="button"
                  onClick={() => setExpandedId(expanded ? null : entry.id)}
                  className="w-full p-3 flex items-start gap-3 text-left"
                  data-testid={`email-history-toggle-${entry.id}`}
                >
                  <div className={`w-8 h-8 rounded-sm flex-shrink-0 flex items-center justify-center ${
                    isSent ? "bg-[#059669]" : "bg-[#DC2626]"
                  }`}>
                    {isSent ? (
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    ) : (
                      <XCircle className="w-4 h-4 text-white" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs tracking-[0.15em] uppercase font-bold ${
                        isSent ? "text-[#059669]" : "text-[#DC2626]"
                      }`}>
                        {isSent ? "Sent" : "Failed"}
                      </span>
                      <span className="text-xs text-[#71717A]">{formatDT(entry.sent_at)}</span>
                      {entry.is_update && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-[#F4F4F5] text-[#71717A] border border-[#E4E4E7]">
                          Update
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-[#09090B] font-medium truncate mt-1">
                      To: {entry.recipient}
                    </div>
                    <div className="text-xs text-[#71717A] truncate mt-0.5">
                      {entry.subject}
                    </div>
                    <div className="flex items-center gap-3 mt-1.5">
                      {entry.attachments_count > 0 && (
                        <span className="text-[10px] text-[#71717A] flex items-center gap-1">
                          <ImageIcon className="w-3 h-3" />
                          {entry.attachments_count} photo{entry.attachments_count > 1 ? "s" : ""}
                        </span>
                      )}
                      {entry.pdf_attached && (
                        <span className="text-[10px] text-[#71717A] flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          PDF attached
                        </span>
                      )}
                    </div>
                  </div>
                  {expanded ? (
                    <ChevronUp className="w-4 h-4 text-[#71717A] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-[#71717A] flex-shrink-0" />
                  )}
                </button>

                {expanded && (
                  <div className="border-t border-[#E4E4E7] p-3 space-y-2 text-xs" data-testid={`email-history-expanded-${entry.id}`}>
                    <div>
                      <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A] mb-1">Engineer</div>
                      <div className="text-[#09090B]">{entry.engineer_name || "N/A"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A] mb-1">Subject Line</div>
                      <div className="text-[#09090B] break-words">{entry.subject}</div>
                    </div>
                    {entry.error && (
                      <div>
                        <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#DC2626] mb-1">Error</div>
                        <div className="text-[#DC2626] break-words">{entry.error}</div>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A] mb-1">Photos</div>
                        <div className="text-[#09090B]">{entry.attachments_count}</div>
                      </div>
                      <div>
                        <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#71717A] mb-1">PDF Report</div>
                        <div className="text-[#09090B]">{entry.pdf_attached ? "Yes" : "No"}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EmailHistory;
